import { GetItemCommand, PutItemCommand, QueryCommand, QueryCommandInput, QueryCommandOutput, ScanCommand, ScanCommandInput, UpdateItemCommand, UpdateItemCommandInput, UpdateItemInput } from "@aws-sdk/client-dynamodb";
import { client, dbClient, sesClient, lambdaClient } from "./awsClients";
import awsConfig from '../amplifyconfiguration.json'
import { DEVICE_CHANGE_LOG_TABLE, DEVICE_TABLE, HOSPITAL_DEVICE_TABLE, HOSPITALS_TABLE, INDIVIDUAL_DEVICE_TABLE, USER_INFO_TABLE } from "@constants/dynamoDBTableName";
import { AdminGetUserCommand, AdminUpdateUserAttributesCommand } from "@aws-sdk/client-cognito-identity-provider";
import { unmarshall } from '@aws-sdk/util-dynamodb';
import { ADMIN, CONFIRMED, DEVICE_ID_CREATED_AT_INDEX, FORCE_CHANGE_PASSWORD, HOSPITAL, HOSPITAL_ID_CREATED_AT_INDEX, INDIVIDUAL, PATIENT_ID_CREATED_AT_INDEX, TENANT_ID_CREATED_AT_INDEX, UNCONFIRMED, USER_ID_CREATED_AT_INDEX } from "@constants/constants";
import { COGNITO_USER_EMAIL, USER_MOBILE_NUMBER } from "@constants/cognitoAttributes";
import { API_KEY } from "@constants/AppConfig";
import { InvokeCommand } from "@aws-sdk/client-lambda";
import { SIGNUP_EMAIL_API } from "@constants/auth/Api";

interface UserAttribute {
  Name: string;
  Value: string;
}

export const generateUniqueTimestamp = (function () {
  let lastTimestamp = 0;

  return function () {
    let currentTimestamp = Date.now();

    // If the current timestamp is the same as the last one, increment it by 1 millisecond
    if (currentTimestamp <= lastTimestamp) {
      currentTimestamp = lastTimestamp + 1;
    }

    lastTimestamp = currentTimestamp;
    return new Date(currentTimestamp).toISOString();
  };
})();

export async function userVerification(emailID: string, checkCondition: boolean) {

  const input = {
    UserPoolId: awsConfig.aws_user_pools_id,
    Username: emailID,
  };

  const command = new AdminGetUserCommand(input);

  return new Promise((resolve, reject) => {
    client.send(command, function (err, data) {
      if (err) {
        console.log('err', err);
        if (err.name = "UserNotFoundException") {
          if (!checkCondition) {
            reject({ name: 'NotAuthorizedException' })
          } else {
            resolve(void 0)
          }
        } else {
          reject({ name: err.name })
        }
      } else {
        const payloadString = JSON.stringify(data, null, 2)

        if (payloadString) {
          const response = JSON.parse(payloadString);
          const emailObject = response.UserAttributes.find((attr: UserAttribute) => attr.Name === 'email');
          const email = emailObject ? emailObject.Value : null;
          if (response.UserStatus == FORCE_CHANGE_PASSWORD) {
            reject({ name: 'FORCE_CHANGE_PASSWORD' })
          } else if ((!response.UserStatus && checkCondition) || (response.UserStatus == CONFIRMED && !checkCondition)) {
            resolve(void 0)
          } else if ((response.UserStatus == UNCONFIRMED && !checkCondition && email != null)) {
            reject({ name: 'UserNotConfirmedException' })

          } else {
            reject({ name: response.UserStatus == CONFIRMED ? 'UsernameExistsException' : 'NotAuthorizedException' })
          }
        } else {
          reject('Payload is undefined')
        }
      }
    })
  })
}

export async function getDataUsingTenantId(tableName: string, tenantId: string): Promise<QueryCommandOutput> {
  if (!tenantId) {
    return { Items: [], Count: 0 } as any;
  }
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: TENANT_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'tenant_id = :tenant_id',
    FilterExpression: "deleted_at = :deleted_at",
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId },
      ':deleted_at': { S: '' }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    return response
  } catch (error: any) {
    throw error;
  }
}

export async function getDataUsingUserId(tableName: string, userId: string): Promise<QueryCommandOutput> {
  if (!userId) {
    return { Items: [], Count: 0 } as any;
  }
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: USER_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'user_id = :user_id',
    FilterExpression: "deleted_at = :deleted_at",
    ExpressionAttributeValues: {
      ':user_id': { S: userId },
      ':deleted_at': { S: '' }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    return response
  } catch (error: any) {
    throw error;
  }
}

export async function getDataUsingHospitalId(tableName: string, hospitalId: string): Promise<QueryCommandOutput> {
  if (!hospitalId) {
    return { Items: [], Count: 0 } as any;
  }
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: HOSPITAL_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'hospital_id = :hospital_id',
    ExpressionAttributeValues: {
      ':hospital_id': { S: hospitalId },
      ':deleted_at': { S: '' },
    },
    FilterExpression: 'deleted_at = :deleted_at',
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    return response
  } catch (error: any) {
    throw error;
  }
}

export async function getDataUsingDeviceId(tableName: string, deviceId: string): Promise<QueryCommandOutput> {
  if (!deviceId) {
    return { Items: [], Count: 0 } as any;
  }
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: DEVICE_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'device_id = :device_id',
    FilterExpression: "deleted_at = :deleted_at",
    ExpressionAttributeValues: {
      ':device_id': { S: deviceId },
      ':deleted_at': { S: '' }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    return response
  } catch (error: any) {
    throw error;
  }
}

export async function getDataUsingPatientId(tableName: string, deviceId: string): Promise<QueryCommandOutput> {
  if (!deviceId) {
    return { Items: [], Count: 0 } as any;
  }
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: PATIENT_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'patient_id = :patient_id',
    ExpressionAttributeValues: {
      ':patient_id': { S: deviceId }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    return response
  } catch (error: any) {
    throw error;
  }
}

export async function getDataByDeviceId(tableName: string, uuid: string) {
  if (!uuid) {
    return null;
  }
  const params = {
    TableName: tableName,
    Key: {
      device_id: { N: uuid }, // 'S' indicates a string attribute type
    },
  };

  try {
    const command = new GetItemCommand(params);
    const response = await dbClient.send(command);
    if (response.Item) {
      return response.Item;
    } else {
      return null;
    }
  } catch (error: any) {
    // Switch case for error handling
    switch (error.name) {
      case 'ResourceNotFoundException':
        console.error("Error: The specified table does not exist.");
        break;
      case 'ProvisionedThroughputExceededException':
        console.error("Error: The request rate is too high. Please try again later.");
        break;
      case 'ValidationException':
        console.error("Error: The provided key element does not match the schema.");
        break;
      case 'DynamoDBServiceException':
        console.error(`Service Error: ${error.message}`);
        break;
      default:
        console.error("An unknown error occurred:", error);
        break;
    }

    // Optionally rethrow the error if you want it to propagate
    throw error;
  }
}

export function capitalizeFirstLetter(title: string) {
  if (!title) return '';

  return title
    .split(' ')
    .map((word) => {
      if (word.toLowerCase() === 'id') {
        return word.toUpperCase();
      } else {
        return (
          word.charAt(0).toUpperCase() +
          word.slice(1).toLowerCase()
        );
      }
    })
    .join(' ');
};

export function formatDateDMY(dateString: any) {
  // Create a Date object from the input string
  const date = new Date(dateString);

  // Extract the day, month, and year from the Date object
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = date.getUTCFullYear();

  // Format the date as DD/MM/YYYY
  return `${day}/${month}/${year}`;
}

export async function userInfo(tenantId: string) {
  const params = {
    TableName: USER_INFO_TABLE,
    IndexName: 'tenant_id-created_at-index',
    KeyConditionExpression: 'tenant_id = :tenant_id',
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    if (response.Items && response.Items.length != 0) {
      return response.Items[0]
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
}

export async function userDetail(userId: string) {
  const params = {
    TableName: USER_INFO_TABLE,
    IndexName: 'user_id-created_at-index',
    KeyConditionExpression: 'user_id = :user_id',
    FilterExpression: "deleted_at = :deleted_at",
    ExpressionAttributeValues: {
      ':user_id': { S: userId },
      ':deleted_at': { S: '' }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    if (response.Items && response.Items.length != 0) {
      return response.Items[0]
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
}

export async function adminUpdateUser(name: string, userData: any) {

  const attributes = Object.keys(userData).map(key => ({
    Name: key,
    Value: userData[key],
  }));

  const input = {
    UserPoolId: awsConfig.aws_user_pools_id,
    Username: name,
    UserAttributes: attributes
  };

  try {
    const command = new AdminUpdateUserAttributesCommand(input);
    const response = await client.send(command);

    return response;
  } catch (error: any) {
    throw error;
  }

}

export async function updateUser(tableName: string, id: string, attrabutiesNames: any, attributValues: any, UpdateExpression: string) {
  const input: UpdateItemCommandInput = {
    TableName: tableName, // Replace with your table name
    Key: {
      uuid: { S: id }, // Replace with your primary key
    },
    ExpressionAttributeNames: attrabutiesNames,
    ExpressionAttributeValues: attributValues,
    UpdateExpression: UpdateExpression,
    ReturnValues: "ALL_NEW"
  };

  const command = new UpdateItemCommand(input);

  try {
    const response = await dbClient.send(command);
    return response;
  } catch (error: any) {
    throw error;
  }
};

export async function getDataUuid(tableName: string, uuid: string) {
  if (!uuid) {
    return null;
  }
  const params = {
    TableName: tableName,
    Key: {
      uuid: { S: uuid }
    },
  };

  try {
    const command = new GetItemCommand(params);
    const response = await dbClient.send(command);
    if (response.Item) {
      return response.Item;
    } else {
      return null;
    }
  } catch (error) {
    throw error;
  }
}

export async function updateTable(tableName: string, key: any, updateExpression: string, expressionAttributeNames: any, expressionAttributeValues: any) {
  const params: UpdateItemInput = {
    TableName: tableName,
    Key: key,
    UpdateExpression: updateExpression,
    ExpressionAttributeNames: expressionAttributeNames,
    ExpressionAttributeValues: expressionAttributeValues,
    ReturnValues: "ALL_NEW" // Optional parameter to return updated item
  };

  try {
    const command = new UpdateItemCommand(params);
    const response = await dbClient.send(command);
    return response.Attributes; // Return the updated item attributes if needed
  } catch (error) {
    console.error(`Error updating item in table ${tableName}:`, error);
    throw error;
  }
}


export async function nurseDeviceData(uuid: string) {
  const pageSize = 10; // Adjust the page size as per your requirement
  let deviceData = [];
  let lastEvaluatedKey = undefined;

  try {
    do {
      const params: QueryCommandInput = {
        TableName: 'nurse_device',
        IndexName: 'device_id-created_at-index',
        KeyConditionExpression: 'device_id = :device_id',
        ExpressionAttributeValues: {
          ':device_id': { S: uuid }
        },
        ExclusiveStartKey: lastEvaluatedKey
      };

      const command = new QueryCommand(params);
      const response = await dbClient.send(command);

      if (response.Items && response.Items.length > 0) {
        const result = response.Items
          .filter(item => !item.deleted_at?.S)

        deviceData.push(...result);
      }

      lastEvaluatedKey = response.LastEvaluatedKey;

    } while (lastEvaluatedKey);

    return deviceData;

  } catch (error) {
    console.error('Error scanning hospital info:', error);
    throw error;
  }
}


export async function getHospitalAdminList(tenantId: string): Promise<any[]> {
  const params: QueryCommandInput = {
    TableName: 'user_info',
    IndexName: TENANT_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'tenant_id = :tenant_id',
    FilterExpression: "deleted_at = :deleted_at AND #role = :role",
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId },
      ':role': { S: HOSPITAL },
      ':deleted_at': { S: '' }
    },
    ExpressionAttributeNames: {
      '#role': 'role'
    }
  };

  try {
    const command = new QueryCommand(params);
    const response: QueryCommandOutput = await dbClient.send(command);

    // Unmarshal the results
    const items = response.Items || [];
    const unmarshalledItems = items.map(item => unmarshall(item));

    // Extract and order attributes
    const orderedResults = unmarshalledItems.map(item => ({
      id: item.uuid || '',
      name: item.name || '',
      email: item.email || '',
      mobile_number: item.mobile_number || '',
      role: item.role || '',
      user_id: item.user_id || ''
    }));

    return orderedResults;
    // return response
  } catch (error: any) {
    return [];
    // throw error;
  }
}

export async function getActiveDeviceData(tableName: string, deviceId: string): Promise<QueryCommandOutput> {
  const params: QueryCommandInput = {
    TableName: tableName,
    IndexName: DEVICE_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'device_id = :device_id',
    ExpressionAttributeValues: {
      ':device_id': { N: deviceId }
    },
    ScanIndexForward: false
  };

  try {
    const command = new QueryCommand(params);
    const response = await dbClient.send(command);
    return response
  } catch (error: any) {
    console.log('error', error);
    throw error;
  }
}

export async function getActivityLogList(role: string, tenantId: string, limit: number = 5, lastEvaluatedKey?: any) {
  try {
    if (role.toLowerCase() === HOSPITAL.toLowerCase() || role.toLowerCase() === INDIVIDUAL.toLowerCase()) {
      let deviceItems: any[] = [];
      if (role.toLowerCase() === HOSPITAL.toLowerCase()) {
        const hospitalData = await getDataUsingTenantId(HOSPITALS_TABLE, tenantId);
        if (hospitalData.Items && hospitalData.Items.length > 0) {
          const hospitalUuid = hospitalData.Items[0].uuid?.S;
          if (hospitalUuid) {
            const hospitalDeviceData = await getDataUsingHospitalId(HOSPITAL_DEVICE_TABLE, hospitalUuid);
            if (hospitalDeviceData.Items) {
              deviceItems = hospitalDeviceData.Items;
            }
          }
        }
      } else {
        const individualDeviceData = await getDataUsingTenantId(INDIVIDUAL_DEVICE_TABLE, tenantId);
        if (individualDeviceData.Items) {
          deviceItems = individualDeviceData.Items;
        }
      }

      if (deviceItems.length === 0) {
        return { logLists: [], lastEvaluatedKey: undefined };
      }

      let allLogs: any[] = [];
      for (const data of deviceItems) {
        const deviceId = data.device_id?.S;
        if (deviceId) {
          const getItemParams = {
            TableName: DEVICE_TABLE,
            Key: { 'uuid': { S: deviceId } }
          };
          const deviceData = await dbClient.send(new GetItemCommand(getItemParams));
          if (deviceData.Item && deviceData.Item.device_id && deviceData.Item.device_id.S) {
            const params: QueryCommandInput = {
              TableName: DEVICE_CHANGE_LOG_TABLE,
              IndexName: DEVICE_ID_CREATED_AT_INDEX,
              KeyConditionExpression: 'device_id = :device_id',
              ExpressionAttributeValues: { ':device_id': { S: deviceData.Item.device_id.S } }
            };
            const command = new QueryCommand(params);
            const deviceLogData = await dbClient.send(command);
            if (deviceLogData.Items) {
              allLogs.push(...deviceLogData.Items);
            }
          }
        }
      }

      allLogs.sort((a, b) => new Date(b.created_at.S).getTime() - new Date(a.created_at.S).getTime());

      const startIndex = lastEvaluatedKey ? allLogs.findIndex(log => log.uuid.S === lastEvaluatedKey) + 1 : 0;
      const paginatedLogs = allLogs.slice(startIndex, startIndex + limit);
      const newLastEvaluatedKey = startIndex + limit < allLogs.length ? allLogs[startIndex + limit].uuid.S : undefined;

      const logLists = await Promise.all(paginatedLogs.map(async (list) => {
        const createdAt = list.created_at.S;
        const dateObj = new Date(createdAt);
        const date = `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`;
        let hours = dateObj.getHours();
        const minutes = dateObj.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12 || 12;
        const time = `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
        const user = await getDataUsingUserId(USER_INFO_TABLE, list.user_id.S);
        const userData = user.Items && user.Items.length > 0 ? user.Items[0] : null;
        if (!userData) return null;
        return {
          id: list.uuid.S,
          date,
          time,
          device_id: list.device_id.S,
          name: userData.name.S,
          created_at: list.created_at.S,
          user_id: list.user_id.S
        };
      }));

      return { logLists: logLists.filter(log => log !== null), lastEvaluatedKey: newLastEvaluatedKey };

    } else if (role.toLowerCase() === ADMIN.toLowerCase()) {
      const params: ScanCommandInput = {
        TableName: DEVICE_CHANGE_LOG_TABLE,
        Limit: limit,
        ExclusiveStartKey: lastEvaluatedKey
      };
      const command = new ScanCommand(params);
      const response = await dbClient.send(command);

      if (!response.Items || response.Items.length === 0) {
        return { logLists: [], lastEvaluatedKey: undefined };
      }

      const logLists = await Promise.all(response.Items.map(async (list: Record<string, any>) => {
        const createdAt = list.created_at.S;
        if (createdAt) {
          const dateObj = new Date(createdAt);
          const date = `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1).toString().padStart(2, '0')}/${dateObj.getFullYear()}`;
          let hours = dateObj.getHours();
          const minutes = dateObj.getMinutes().toString().padStart(2, '0');
          const ampm = hours >= 12 ? 'PM' : 'AM';
          hours = hours % 12 || 12;
          const time = `${hours.toString().padStart(2, '0')}:${minutes} ${ampm}`;
          const user = await getDataUsingUserId(USER_INFO_TABLE, list.user_id?.S || '');
          const userData = user.Items && user.Items.length > 0 ? user.Items[0] : null;
          if (!userData) return null;
          return {
            id: list.uuid.S,
            date,
            time,
            device_id: list.device_id.S,
            name: userData.name.S,
            created_at: list.created_at.S,
            user_id: list.user_id.S
          };
        }
        return null;
      }));

      return { logLists: logLists.filter(log => log !== null), lastEvaluatedKey: response.LastEvaluatedKey };
    }
    return { logLists: [], lastEvaluatedKey: undefined };
  } catch (error: any) {
    console.log('error', error);
    throw error;
  }
}

export async function getHospitalUserList(tenantId: string): Promise<any> {
  const params: QueryCommandInput = {
    TableName: USER_INFO_TABLE,
    IndexName: TENANT_ID_CREATED_AT_INDEX,
    KeyConditionExpression: 'tenant_id = :tenant_id',
    FilterExpression: "deleted_at = :deleted_at",
    ExpressionAttributeValues: {
      ':tenant_id': { S: tenantId },
      ':deleted_at': { S: '' }
    },
  };

  try {
    const command = new QueryCommand(params);
    const response: QueryCommandOutput = await dbClient.send(command);

    if (!response.Items) {
      return [];
    }

    // Get user information from Cognito and return only those with verified emails
    const verifiedUsers = await Promise.all(response.Items.map(async (item) => {
      try {
        const getUserCommand = new AdminGetUserCommand({
          UserPoolId: awsConfig.aws_user_pools_id,
          Username: item.user_id.S,
        });
        const user = await client.send(getUserCommand);
        const userId = item.user_id.S;

        const emailVerified = user.UserAttributes?.find(attr => attr.Name === 'email_verified')?.Value === 'true';
        const email = user.UserAttributes?.find(attr => attr.Name === COGNITO_USER_EMAIL)?.Value;
        const name = item.name.S;
        const mobileNumber = user.UserAttributes?.find(attr => attr.Name === USER_MOBILE_NUMBER)?.Value;

        if (emailVerified && email) {
          return { userId, name, email, mobileNumber };
        } else {
          return null; // Skip users without verified emails
        }
      } catch (error) {
        console.error(`Error fetching user ${item.user_id.S}:`, error);
        return null; // Skip users if there is an error
      }
    }));

    // Filter out null values and return only verified users
    return verifiedUsers.filter(user => user !== null);

  } catch (error) {
    throw error;
  }
}

type NotificationData = {
  type: string; 
  users: string[]; 
  fromAddress: string; 
  toAddress: string[]; 
  schedule?: { publishDate: string; publishTime: string } | null;
};

type AssignNotificationData = {
  type: string; 
  users: string; 
  fromAddress: string; 
  toAddress: string; 
  schedule?: { publishDate: string; publishTime: string } | null;
};
type StatusNotificationData = {
  users: string; 
  status: string;
};


export const sendNotification = async (data: NotificationData) => {
  try {
    const response = await fetch(SIGNUP_EMAIL_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': API_KEY,
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
} catch (error) {
    console.error('Error sending notification:', error);
    throw new Error('Failed to send notification');
}
};
export const sendAssignNotification = async (data: AssignNotificationData) => {
  try {
    const command = new InvokeCommand({
      FunctionName: "arn:aws:lambda:ap-south-1:079693644758:function:assignDeviceEmail",
      Payload: JSON.stringify(data),
      InvocationType: 'Event',
    });
    const response = await lambdaClient.send(command);
} catch (error) {
    console.error('Error sending notification:', error);
    throw new Error('Failed to send notification');
}
};
export const sendStatusNotification = async (data: StatusNotificationData) => {
  try {
    const lambdaCommand = new InvokeCommand({
      FunctionName: "arn:aws:lambda:ap-south-1:079693644758:function:accountStatusEmail",
      Payload: JSON.stringify(data),
      InvocationType: 'Event',
    });
    const response = await lambdaClient.send(lambdaCommand);
} catch (error) {
    console.error('Error sending notification:', error);
    throw new Error('Failed to send notification');
}
};

export type NotificationValue = {
  id: string;
  createdAtRaw: string;
  notificationDate: string;
  message: string;
  status: string;
  deviceId: string;
  deviceName: string;
  nurseSummary?: string;
  errorOccurredTime?: string;
  errorResolvedTime?: string;
  resolvedNurseName?: string;
  command?: string;
  errorCode?: string;
};

const formatNotificationTimestamp = (dateStr?: string): string => {
  if (!dateStr) return '--';
  const parsed = new Date(dateStr);
  if (isNaN(parsed.getTime())) return dateStr;
  const day = String(parsed.getDate()).padStart(2, '0');
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = monthNames[parsed.getMonth()];
  const year = parsed.getFullYear();
  let hours = parsed.getHours();
  const minutes = String(parsed.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const formattedHours = String(hours).padStart(2, '0');
  return `${day}-${month}-${year} ${formattedHours}:${minutes} ${ampm}`;
};

export const getAllNotifications = async (lastEvaluatedKey?: any): Promise<{ notifications: NotificationValue[], lastEvaluatedKey: any }> => {
  const TableName = "notification";
  
  try {
    const params: ScanCommandInput = {
      TableName,
      Limit: 10,
      ExclusiveStartKey: lastEvaluatedKey,
    };

    const result = await dbClient.send(new ScanCommand(params));

    const mappedItems: NotificationValue[] =
      (result.Items || []).map((item: any) => ({
        id: item.uuid?.S || "",
        createdAtRaw: item.created_at?.S || "",
        notificationDate: item.created_at?.S ? formatNotificationTimestamp(item.created_at.S) : "",
        message: item.body?.S ? item.body.S : "--",
        deviceName: item.device_name?.S ? item.device_name.S : "--",
        deviceId: item.device_id?.S ? item.device_id.S : "--",
        status: item.status?.S ? item.status.S : "--",
        nurseSummary: item.nurse_summary?.S || "",
        errorOccurredTime: item.error_occurred_time?.S ? formatNotificationTimestamp(item.error_occurred_time.S) : (item.created_at?.S ? formatNotificationTimestamp(item.created_at.S) : "--"),
        errorResolvedTime: item.error_resolved_time?.S ? formatNotificationTimestamp(item.error_resolved_time.S) : (item.updated_at?.S ? formatNotificationTimestamp(item.updated_at.S) : "--"),
        resolvedNurseName: item.resolved_nurse_name?.S || "",
        command: item.command?.S || "",
        errorCode: item.error_code?.S || "",
      }));

    return { notifications: mappedItems, lastEvaluatedKey: result.LastEvaluatedKey };
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

export const getAllUserNotifications = async (tenantId: string, lastEvaluatedKey?: any): Promise<{ notifications: NotificationValue[], lastEvaluatedKey: any }> => {
  const TableName = "notification";
  
  try {
    const params: QueryCommandInput = {
      TableName,
      IndexName: "tenant_id-created_at-index",
      KeyConditionExpression: "tenant_id = :tenantId",
      ExpressionAttributeValues: {
        ":tenantId": { S: tenantId },
      },
      Limit: 10,
      ExclusiveStartKey: lastEvaluatedKey,
      ScanIndexForward: false,
    };

    const result = await dbClient.send(new QueryCommand(params));

    const mappedItems: NotificationValue[] =
      (result.Items || []).map((item: any) => ({
        id: item.uuid?.S || "",
        createdAtRaw: item.created_at?.S || "",
        notificationDate: item.created_at?.S ? formatNotificationTimestamp(item.created_at.S) : "",
        message: item.body?.S ? item.body.S : "--",
        deviceName: item.device_name?.S ? item.device_name.S : "--",
        deviceId: item.device_id?.S ? item.device_id.S : "--",
        status: item.status?.S ? item.status.S : "--",
        nurseSummary: item.nurse_summary?.S || "",
        errorOccurredTime: item.error_occurred_time?.S ? formatNotificationTimestamp(item.error_occurred_time.S) : (item.created_at?.S ? formatNotificationTimestamp(item.created_at.S) : "--"),
        errorResolvedTime: item.error_resolved_time?.S ? formatNotificationTimestamp(item.error_resolved_time.S) : (item.updated_at?.S ? formatNotificationTimestamp(item.updated_at.S) : "--"),
        resolvedNurseName: item.resolved_nurse_name?.S || "",
        command: item.command?.S || "",
        errorCode: item.error_code?.S || "",
      }));

    return { notifications: mappedItems, lastEvaluatedKey: result.LastEvaluatedKey };
  } catch (error) {
    console.error("Error fetching notifications:", error);
    throw error;
  }
};

export const fallbackUpdateNotification = async (payload: { uuid?: string; device_id?: string; nurse_summary?: string; nurse_name?: string; command?: string }): Promise<any> => {
  const { uuid: targetUuid, device_id: deviceId, nurse_summary, nurse_name, command } = payload;
  let targetItemUuid = targetUuid;

  if (!targetItemUuid && deviceId) {
    try {
      const scanParams: ScanCommandInput = {
        TableName: 'notification',
        FilterExpression: 'device_id = :device_id',
        ExpressionAttributeValues: {
          ':device_id': { S: deviceId }
        }
      };
      const scanRes = await dbClient.send(new ScanCommand(scanParams));
      if (scanRes.Items && scanRes.Items.length > 0) {
        scanRes.Items.sort((a: any, b: any) => {
          const dateA = new Date(a.created_at?.S || 0).getTime();
          const dateB = new Date(b.created_at?.S || 0).getTime();
          return dateB - dateA;
        });
        targetItemUuid = scanRes.Items[0].uuid?.S;
      }
    } catch (e) {
      console.error('Error scanning latest notification in fallback:', e);
    }
  }

  if (!targetItemUuid) {
    return { message: 'Notification record not found' };
  }

  let updateExpression = "SET updated_at = :updated_at";
  const expressionAttributeValues: Record<string, any> = {
    ":updated_at": { S: new Date().toISOString() }
  };
  const expressionAttributeNames: Record<string, string> = {};

  if (nurse_summary !== undefined) {
    updateExpression += ", nurse_summary = :nurse_summary";
    expressionAttributeValues[":nurse_summary"] = { S: String(nurse_summary) };

    if (nurse_name) {
      updateExpression += ", resolved_nurse_name = :resolved_nurse_name";
      expressionAttributeValues[":resolved_nurse_name"] = { S: String(nurse_name) };
    }
  }

  if (command !== undefined) {
    updateExpression += ", #cmd = :command";
    expressionAttributeNames["#cmd"] = "command";
    expressionAttributeValues[":command"] = { S: String(command) };
  }

  const updateParams: UpdateItemCommandInput = {
    TableName: 'notification',
    Key: {
      uuid: { S: targetItemUuid }
    },
    UpdateExpression: updateExpression,
    ExpressionAttributeValues: expressionAttributeValues,
    ...(Object.keys(expressionAttributeNames).length > 0 ? { ExpressionAttributeNames: expressionAttributeNames } : {})
  };

  await dbClient.send(new UpdateItemCommand(updateParams));
  return { message: 'Notification updated successfully', uuid: targetItemUuid };
};